import {
  TenantActions,
  TenantActionTypes,
} from '@rd/core/tenant/store/actions/tenant.actions';
import { AutoRedemptionRule } from '@rd/types/auto-redemption-rule.type';
import { AutoRedemptionSetting } from '@rd/types/auto-redemption-setting.type';
import { HttpError } from '@rd/types/http-error.type';
import {
  CreateCashBackAutoRedemptionSetting,
  CreateDigitalGiftCardAutoRedemptionSetting,
  CreatePhysicalGiftCardAutoRedemptionSetting,
  CreatePointsTransferAutoRedemptionSetting,
} from '../../services/auto-redemption-setting-api/auto-redemption-setting-api.service';
import {
  AutoRedemptionSettingsActions,
  AutoRedemptionSettingTypes,
} from '../actions/auto-redemption-settings.actions';

export interface AutoRedemptionSettingsState {
  formData: {
    [id: string]:
      | CreatePointsTransferAutoRedemptionSetting
      | CreateCashBackAutoRedemptionSetting
      | CreateDigitalGiftCardAutoRedemptionSetting
      | CreatePhysicalGiftCardAutoRedemptionSetting;
  };
  autoRedemptionSettings: {
    loading: boolean;
    error: HttpError | null;
    autoRedemptionSetting: AutoRedemptionSetting | null;
  };
  createAutoRedemptionSettings: {
    loading: boolean;
    error: HttpError | null;
    success: boolean;
  };
  deleteAutoRedemptionSettings: {
    loading: boolean;
    error: HttpError | null;
    success: boolean;
  };
  updateAutoRedemptionSettings: {
    loading: boolean;
    error: HttpError | null;
    success: boolean;
  };
  autoRedemptionRules: AutoRedemptionRule[];
}

export const initialState: AutoRedemptionSettingsState = {
  formData: {},
  autoRedemptionSettings: {
    loading: false,
    error: null,
    autoRedemptionSetting: null,
  },
  createAutoRedemptionSettings: {
    loading: false,
    error: null,
    success: false,
  },
  deleteAutoRedemptionSettings: {
    loading: false,
    error: null,
    success: false,
  },
  updateAutoRedemptionSettings: {
    loading: false,
    error: null,
    success: false,
  },
  autoRedemptionRules: [],
};

export function autoRedemptionSettingReducer(
  state: AutoRedemptionSettingsState = initialState,
  action: AutoRedemptionSettingsActions | TenantActions,
): AutoRedemptionSettingsState {
  switch (action.type) {
    case TenantActionTypes.LOAD_BOOTSTRAP_SUCCESS:
      return {
        ...state,
        autoRedemptionRules: action.payload.autoRedemptionRules,
      };
    case AutoRedemptionSettingTypes.LOAD_AUTO_REDEMPTION_SETTINGS: {
      // We reset the notifications when we re-load auto redemptions settings
      return {
        ...state,
        deleteAutoRedemptionSettings: {
          ...state.deleteAutoRedemptionSettings,
          loading: false,
          success: false,
          error: null,
        },
        createAutoRedemptionSettings: {
          ...state.createAutoRedemptionSettings,
          loading: false,
          success: false,
          error: null,
        },
        updateAutoRedemptionSettings: {
          ...state.updateAutoRedemptionSettings,
          loading: false,
          success: false,
          error: null,
        },
        autoRedemptionSettings: {
          ...state.autoRedemptionSettings,
          loading: true,
          error: null,
        },
      };
    }
    case AutoRedemptionSettingTypes.LOAD_AUTO_REDEMPTION_SETTINGS_SUCCESS: {
      return {
        ...state,
        autoRedemptionSettings: {
          ...state.autoRedemptionSettings,
          loading: false,
          autoRedemptionSetting:
            action.payload.length > 0 ? action.payload[0] : null,
        },
      };
    }
    case AutoRedemptionSettingTypes.LOAD_AUTO_REDEMPTION_SETTINGS_FAIL: {
      return {
        ...state,
        autoRedemptionSettings: {
          ...state.autoRedemptionSettings,
          loading: false,
          error: action.payload,
        },
      };
    }
    case AutoRedemptionSettingTypes.DELETE_AUTO_REDEMPTION_SETTING: {
      return {
        ...state,
        updateAutoRedemptionSettings: {
          loading: false,
          success: false,
          error: null,
        },
        createAutoRedemptionSettings: {
          loading: false,
          success: false,
          error: null,
        },
        deleteAutoRedemptionSettings: {
          ...state.deleteAutoRedemptionSettings,
          loading: true,
          success: false,
          error: null,
        },
      };
    }
    case AutoRedemptionSettingTypes.DELETE_AUTO_REDEMPTION_SETTING_SUCCESS: {
      return {
        ...state,
        autoRedemptionSettings: {
          ...state.autoRedemptionSettings,
          autoRedemptionSetting: null,
        },
        deleteAutoRedemptionSettings: {
          ...state.deleteAutoRedemptionSettings,
          loading: false,
          success: true,
          error: null,
        },
      };
    }
    case AutoRedemptionSettingTypes.DELETE_AUTO_REDEMPTION_SETTING_FAIL: {
      return {
        ...state,
        deleteAutoRedemptionSettings: {
          ...state.deleteAutoRedemptionSettings,
          loading: false,
          success: false,
          error: action.payload,
        },
      };
    }
    case AutoRedemptionSettingTypes.UPDATE_AUTO_REDEMPTION_SETTING: {
      return {
        ...state,
        deleteAutoRedemptionSettings: {
          success: false,
          loading: false,
          error: null,
        },
        updateAutoRedemptionSettings: {
          success: false,
          loading: true,
          error: null,
        },
      };
    }
    case AutoRedemptionSettingTypes.UPDATE_AUTO_REDEMPTION_SETTING_SUCCESS: {
      return {
        ...state,
        autoRedemptionSettings: {
          ...state.autoRedemptionSettings,
          autoRedemptionSetting: action.payload,
        },
        updateAutoRedemptionSettings: {
          success: true,
          error: null,
          loading: false,
        },
      };
    }
    case AutoRedemptionSettingTypes.UPDATE_AUTO_REDEMPTION_SETTING_FAIL: {
      return {
        ...state,
        updateAutoRedemptionSettings: {
          loading: false,
          success: false,
          error: action.payload,
        },
      };
    }

    case AutoRedemptionSettingTypes.CREATE_AUTO_REDEMPTION_SETTING: {
      return {
        ...state,
        updateAutoRedemptionSettings: {
          success: false,
          loading: false,
          error: null,
        },
        deleteAutoRedemptionSettings: {
          success: false,
          loading: false,
          error: null,
        },
        createAutoRedemptionSettings: {
          success: false,
          loading: true,
          error: null,
        },
      };
    }

    case AutoRedemptionSettingTypes.CREATE_AUTO_REDEMPTION_SETTING_SUCCESS: {
      return {
        ...state,
        formData: {},
        createAutoRedemptionSettings: {
          success: true,
          loading: false,
          error: null,
        },
        autoRedemptionSettings: {
          ...state.autoRedemptionSettings,
          autoRedemptionSetting: action.payload,
        },
      };
    }
    case AutoRedemptionSettingTypes.CREATE_AUTO_REDEMPTION_SETTING_FAIL: {
      return {
        ...state,
        createAutoRedemptionSettings: {
          success: false,
          loading: false,
          error: action.payload,
        },
      };
    }
    case AutoRedemptionSettingTypes.SUBMIT_AUTO_REDEMPTION_SETTING_FORM: {
      return {
        ...state,
        formData: {
          ...state.formData,
          [action.payload.product_id]: action.payload,
        },
      };
    }

    case AutoRedemptionSettingTypes.CLEAR_AUTO_REDEMPTION_SETTING_FORM: {
      return {
        ...state,
        formData: {},
      };
    }

    case AutoRedemptionSettingTypes.HYDRATE_AND_UPDATE_AUTO_REDEMPTION_SETTING: {
      return {
        ...state,
        deleteAutoRedemptionSettings: {
          success: false,
          loading: false,
          error: null,
        },
        updateAutoRedemptionSettings: {
          success: false,
          error: null,
          loading: true,
        },
      };
    }

    case AutoRedemptionSettingTypes.RESET_AUTO_REDEMPTION_SETTING_STATE: {
      return {
        ...state,
        autoRedemptionSettings: {
          ...state.autoRedemptionSettings,
          loading: false,
          error: null,
        },
        createAutoRedemptionSettings: {
          ...state.createAutoRedemptionSettings,
          loading: false,
          success: false,
        },
        deleteAutoRedemptionSettings: {
          ...state.deleteAutoRedemptionSettings,
          loading: false,
          success: false,
        },
        updateAutoRedemptionSettings: {
          ...state.updateAutoRedemptionSettings,
          loading: false,
          success: false,
        },
      };
    }

    default: {
      return state;
    }
  }
}
